import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import { DataGridPro, GridActionsCellItem, GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid-pro';
import { TrashIcon } from '../../../../components/icons/trash-icon/trash-icon';
import { ForumGridOverlay } from '../forum-grid-overlay';

export interface INudgeCitizen {
  citizenId: string;
  name: string;
  cpr?: string;
  hasApp?: boolean;
  appStatusFailed?: boolean;
}

export interface INudgeCitizenListProps {
  citizens: INudgeCitizen[];
  onRemove: (citizenId: string) => void;
}

function NoCitizens() {
  return <ForumGridOverlay message="Tilføj de borgere, du vil nudge." />;
}

const components = { NoRowsOverlay: NoCitizens };

const maxRowsBeforeScrolling = 10;
const scrollingGridHeight = 440;

export function NudgeCitizenList(props: INudgeCitizenListProps) {
  const { citizens, onRemove } = props;

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'name',
        headerName: 'Navn',
        flex: 1,
      },
      {
        field: 'cpr',
        headerName: 'CPR-nummer',
        width: 150,
      },
      {
        field: 'hasApp',
        headerName: 'Har app',
        width: 150,
        renderCell: ({ row }: GridRenderCellParams<unknown, INudgeCitizen>) => <span data-testid={`has-app-${row.citizenId}`}>{hasAppText(row)}</span>,
      },
      {
        field: 'tools',
        type: 'actions',
        headerName: 'Værktøjer',
        width: 120,
        getActions: ({ row }: GridRowParams<INudgeCitizen>) => [
          <GridActionsCellItem
            key="remove"
            label={`Fjern ${row.name}`}
            title={`Fjern ${row.name}`}
            icon={<TrashIcon />}
            onClick={() => onRemove(row.citizenId)}
            placeholder=""
            onPointerEnterCapture={undefined}
            onPointerLeaveCapture={undefined}
          />,
        ],
      },
    ],
    [onRemove]
  );

  const scrolls = citizens.length > maxRowsBeforeScrolling;

  return (
    <Box height={scrolls ? scrollingGridHeight : undefined}>
      <DataGridPro<INudgeCitizen>
        rows={citizens}
        columns={columns}
        columnBuffer={columns.length}
        getRowId={citizen => citizen.citizenId}
        autoHeight={!scrolls}
        density="compact"
        components={components}
        disableSelectionOnClick
      />
    </Box>
  );
}

function hasAppText(citizen: INudgeCitizen) {
  if (citizen.appStatusFailed) {
    return 'Kunne ikke hentes';
  }
  if (citizen.hasApp === undefined) {
    return '…';
  }
  return citizen.hasApp ? 'Ja' : 'Nej';
}
